import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AuthApiService } from '../../core/auth/auth-api.service';
import * as AuthActions from './auth.actions';
import { decodeJwtClaims, isJwtExpired } from '../../core/auth/jwt';
import { writeStoredAccessToken } from '../../core/auth/token-storage';
import { selectAccessToken, selectIsAuthCheckNeeded } from './auth.selectors';
import { ApiService } from '../../core/api/api.service';
import { ReauthDialogService } from '../../shared/components/reauth-dialog/reauth-dialog.service';
import { PushNotificationsService } from '../../core/services/push-notifications.service';

@Injectable()
export class AuthEffects {
    constructor(
        private actions$: Actions,
        private authApi: AuthApiService,
        private store: Store,
        private router: Router,
        private api: ApiService,
        private reauthDialog: ReauthDialogService,
        private pushNotifications: PushNotificationsService
    ) { }

    syncActorWithAccessToken$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.setAccessToken),
            mergeMap(({ accessToken }) => {
                if (!accessToken) {
                    return of(
                        AuthActions.setActor({ actor: null }),
                        AuthActions.loadCurrentUserSuccess({ user: null })
                    );
                }
                const claims = decodeJwtClaims(accessToken);
                const userId = claims?.sub ? String(claims.sub) : null;
                if (!claims || !userId) {
                    return of(
                        AuthActions.setActor({ actor: null }),
                        AuthActions.loadCurrentUserFailure({ error: 'Missing access token' })
                    );
                }

                const role = claims?.role ? String(claims.role).toLowerCase() : null;
                const actorType = role === 'investor' ? 'investor' : 'org';
                const actor = {
                    actor_type: actorType,
                    user_id: userId,
                    org_id: claims?.org_id ? String(claims.org_id) : null,
                    role_id: claims?.role_id ? String(claims.role_id) : null,
                    role_code: role,
                    permission_version: typeof claims?.perm_ver === 'number' ? claims.perm_ver : null
                };

                return of(
                    AuthActions.setActor({ actor }),
                    AuthActions.loadCurrentUser()
                );
            })
        )
    );

    persistAccessToken$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.setAccessToken, AuthActions.clearAuthState),
                tap((action: any) => {
                    if (action?.type === AuthActions.clearAuthState.type) {
                        writeStoredAccessToken(null);
                        return;
                    }
                    writeStoredAccessToken(action?.accessToken ?? null);
                })
            ),
        { dispatch: false }
    );

    checkAuth$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.checkAuth),
            withLatestFrom(this.store.select(selectIsAuthCheckNeeded), this.store.select(selectAccessToken)),
            switchMap(([action, needsCheck, accessToken]) => {
                const forceCheck = (action as any).force;
                const claims = decodeJwtClaims(accessToken);
                const hasValidAccessToken = Boolean(accessToken && claims && !isJwtExpired(claims));

                if (hasValidAccessToken && !forceCheck) {
                    return of(
                        AuthActions.checkAuthSuccess({ isAuthenticated: true }),
                        AuthActions.loadCurrentUser()
                    );
                }

                if (!needsCheck && !forceCheck) {
                    return of(AuthActions.checkAuthSuccess({ isAuthenticated: hasValidAccessToken }));
                }

                return this.authApi.refresh().pipe(
                    mergeMap((session) => [
                        AuthActions.setAccessToken({ accessToken: session.access_token }),
                        AuthActions.checkAuthSuccess({ isAuthenticated: true }),
                        AuthActions.loadCurrentUser()
                    ]),
                    catchError(() =>
                        of(
                            AuthActions.setAccessToken({ accessToken: null }),
                            AuthActions.setActor({ actor: null }),
                            AuthActions.checkAuthSuccess({ isAuthenticated: false })
                        )
                    )
                );
            })
        )
    );

    loadCurrentUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.loadCurrentUser),
            withLatestFrom(this.store.select(selectAccessToken)),
            switchMap(([, accessToken]) => {
                const claims = decodeJwtClaims(accessToken);
                const userId = claims?.sub ? String(claims.sub) : null;
                if (!accessToken || !claims || !userId) {
                    return of(
                        AuthActions.setActor({ actor: null }),
                        AuthActions.loadCurrentUserFailure({ error: 'Missing access token' })
                    );
                }

                return this.api.get<any>('/API2/users/me').pipe(
                    mergeMap((user) => of(
                        AuthActions.loadCurrentUserSuccess({ user })
                    )),
                    catchError(() => of(
                        AuthActions.loadCurrentUserSuccess({ user: { id: userId } })
                    ))
                );
            })
        )
    );

    signOut$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.signOut),
            switchMap(({ redirectTo }) => {
                const fcmToken = this.pushNotifications.getCurrentToken();
                return this.authApi.signOut(fcmToken).pipe(
                    catchError(() => of({ ok: true as const })),
                    mergeMap(() => [
                        AuthActions.setAccessToken({ accessToken: null }),
                        AuthActions.setActor({ actor: null }),
                        AuthActions.signOutSuccess()
                    ]),
                    tap(() => {
                        const target = redirectTo || '/';
                        this.router.navigateByUrl(target).catch(() => { });
                    })
                );
            })
        )
    );

    redirectToLogin$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.redirectToLogin),
                tap(({ returnUrl }) => {
                    const currentUrl = this.router.url || '/';
                    if (currentUrl.includes('/login') || currentUrl.includes('/signup')) return;
                    this.router.navigate(['/login'], { queryParams: { returnUrl: returnUrl || currentUrl } }).catch(() => { });
                })
            ),
        { dispatch: false }
    );

    sessionExpired$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.sessionExpired),
                tap(({ returnUrl }) => {
                    this.reauthDialog.open({
                        returnUrl,
                        message: 'Your session expired. Please sign in again to continue.'
                    });
                })
            ),
        { dispatch: false }
    );
}
